import { EmployeeDto, TemplateDto } from '@zorro/clients';
import { formatDateISO, getNow } from '@zorro/shared/formatters';
import {
  downloadFile,
  showErrorNotification,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { useMemo } from 'react';

import { buildIchraLetterFileName } from './ichraLetterFileName';

/** Satisfied by both OnboardingPeriodDto and OnboardingPeriodReportDto. */
type IchraOnboardingPeriod = {
  id: string;
  employeeId: string;
  employerId: string;
  effectiveFrom: string;
};

type Props = {
  employee?: Pick<EmployeeDto, 'firstName' | 'lastName'>;
  onboardingPeriod?: IchraOnboardingPeriod;
};

const buildIchraLetterDocument = (
  employee: Pick<EmployeeDto, 'firstName' | 'lastName'>,
  onboardingPeriod: IchraOnboardingPeriod,
  template: TemplateDto
) => {
  return {
    id: template.eid,
    fileName: buildIchraLetterFileName(
      employee,
      onboardingPeriod.effectiveFrom
    ),
    isSharedWithEmployee: true,
    templateId: template.eid,
    onboardingPeriodId: onboardingPeriod.id,
    employerId: onboardingPeriod.employerId,
    employeeId: onboardingPeriod.employeeId,
    createdAt: formatDateISO(getNow()),
    documentUrl: '', // Rendered on demand, so there is no stored file to point at.
  };
};

/**
 * The ICHRA letter has no `benefit_document` row behind it — Anvil renders it per
 * click. This fabricates a document-shaped object so it can sit in the same card
 * list as real files, and pairs it with the generate-then-download handler.
 */
export const useIchraLetterDocument = ({
  employee,
  onboardingPeriod,
}: Props) => {
  const { data: template } = useMonolithQuery({
    method: 'benefitDocumentsControllerGetIchraLetterTemplate',
    params: [],
  });

  // Generation is slow enough to need the full-page overlay, which is on by
  // default. Reloading and invalidating are not: nothing is persisted.
  const { tryMutate: generateIchraLetter } = useMonolithMutation({
    method: 'benefitDocumentsControllerGenerateIchraLetter',
    shouldReloadPage: false,
    shouldInvalidateQueries: false,
  });

  const ichraLetterDocument = useMemo(() => {
    if (!employee || !onboardingPeriod || !template?.eid) {
      return null;
    }
    return buildIchraLetterDocument(employee, onboardingPeriod, template);
  }, [employee, onboardingPeriod, template]);

  const downloadIchraLetter = async () => {
    if (!ichraLetterDocument) {
      return;
    }

    const result = await generateIchraLetter([
      ichraLetterDocument.onboardingPeriodId,
      ichraLetterDocument.employeeId,
      ichraLetterDocument.templateId,
    ]);

    if (result.isOk()) {
      downloadFile(result.value.url, result.value.fileName);
      return;
    }

    showErrorNotification({
      message: 'Failed to generate ICHRA/CHOICE letter',
    });
  };

  return { ichraLetterDocument, downloadIchraLetter };
};
